/**
 * api/integrations.js — cofre de integrações (GitHub / Bitbucket / Jira).
 *
 * Endpoints:
 *   GET    /integrations              → status por provider (sem segredos)
 *   PUT    /integrations/{provider}   → conectar/atualizar { config: {...} }
 *   POST   /integrations/{provider}/test → re-testar credencial salva
 *   DELETE /integrations/{provider}   → desconectar
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

export const integrationKeys = {
  all: () => ['integrations'],
};

/** Metadados de UI por provider: rótulos dos campos e quais são segredos. */
export const INTEGRATION_META = {
  github: {
    label: 'GitHub',
    fields: {
      token: { label: 'Personal Access Token', secret: true, placeholder: 'ghp_… ou github_pat_…' },
    },
  },
  bitbucket: {
    label: 'Bitbucket',
    fields: {
      workspace:    { label: 'Workspace', placeholder: 'meu-workspace' },
      username:     { label: 'Usuário', placeholder: 'seu-usuario' },
      app_password: { label: 'App password', secret: true, placeholder: 'app password do Bitbucket' },
    },
  },
  jira: {
    label: 'Jira',
    fields: {
      domain: { label: 'Domínio', placeholder: 'suaempresa.atlassian.net' },
      email:  { label: 'E-mail', placeholder: 'voce@empresa.com' },
      token:  { label: 'API token', secret: true, placeholder: 'API token do Atlassian' },
    },
  },
  // ── Provedores de IA ──────────────────────────────────────────────────────
  openai: {
    label: 'OpenAI',
    fields: {
      api_key: { label: 'API key', secret: true, placeholder: 'sk-…' },
    },
  },
  anthropic: {
    label: 'Anthropic (Claude)',
    fields: {
      api_key: { label: 'API key', secret: true, placeholder: 'sk-ant-…' },
    },
  },
  google_gemini: {
    label: 'Google Gemini',
    fields: {
      api_key: { label: 'API key', secret: true, placeholder: 'AIza…' },
    },
  },
  azure_openai: {
    label: 'Azure OpenAI',
    fields: {
      endpoint: { label: 'Endpoint', placeholder: 'https://SEU-RECURSO.openai.azure.com' },
      api_key:  { label: 'API key', secret: true, placeholder: 'chave do recurso Azure' },
    },
  },
  bedrock: {
    label: 'Amazon Bedrock',
    fields: {
      region:            { label: 'Região', placeholder: 'us-east-1' },
      access_key_id:     { label: 'Access key ID', placeholder: 'AKIA…' },
      secret_access_key: { label: 'Secret access key', secret: true, placeholder: 'chave secreta AWS' },
    },
  },
  github_copilot: {
    label: 'GitHub Copilot',
    fields: {
      org:   { label: 'Organização', placeholder: 'minha-empresa' },
      token: { label: 'Token (manage_billing:copilot)', secret: true, placeholder: 'ghp_… ou github_pat_…' },
    },
  },
  // Provider só-ambiente: embeddings via Ollama (openai_compat). Não tem fluxo de
  // cofre — vem do .env do AI Core e aparece read-only (source='env').
  ollama: {
    label: 'Embeddings (Ollama)',
    envOnly: true,
    fields: {},
  },
};

export function useIntegrations() {
  const api = useApi();
  return useQuery({
    queryKey: integrationKeys.all(),
    queryFn:  () => api.get('/integrations'),
    staleTime: 30_000,
  });
}

export function useConnectIntegration() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, config }) => api.put(`/integrations/${provider}`, { config }),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.all() }),
  });
}

export function useTestIntegration() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider) => api.post(`/integrations/${provider}/test`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.all() }),
  });
}

export function useDisconnectIntegration() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider) => api.delete(`/integrations/${provider}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.all() }),
  });
}

/**
 * runOAuthPopup — abre `authUrl` num popup e resolve com {code, state} quando a
 * página de callback (GitOAuthCallback) devolve via postMessage (same-origin).
 * A janela principal (opener) mantém o access token vivo em memória, então a troca
 * por token roda aqui no opener. Rejeita se o popup for bloqueado, fechado antes de
 * concluir, o GitHub negar acesso, ou estourar o tempo.
 */
export function runOAuthPopup(authUrl, { timeoutMs = 5 * 60 * 1000 } = {}) {
  return new Promise((resolve, reject) => {
    const w = 640, h = 720;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top  = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(authUrl, 'nava-git-oauth', `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) {
      reject(new Error('Popup bloqueado — permita popups para conectar com o GitHub.'));
      return;
    }
    let done = false;
    const origin = window.location.origin;
    const cleanup = () => {
      window.removeEventListener('message', onMsg);
      clearInterval(poll);
      clearTimeout(timer);
    };
    const onMsg = (e) => {
      if (e.origin !== origin || !e.data || e.data.source !== 'nava-git-oauth') return;
      done = true;
      cleanup();
      try { popup.close(); } catch { /* ignore */ }
      const { code, state, error } = e.data;
      if (error) reject(new Error(error === 'access_denied' ? 'Autorização cancelada no GitHub.' : String(error)));
      else if (code && state) resolve({ code, state });
      else reject(new Error('Retorno do GitHub sem code/state.'));
    };
    window.addEventListener('message', onMsg);
    const poll = setInterval(() => {
      if (popup.closed && !done) { cleanup(); reject(new Error('Janela do GitHub fechada antes de concluir.')); }
    }, 500);
    const timer = setTimeout(() => {
      if (!done) { cleanup(); try { popup.close(); } catch { /* ignore */ } reject(new Error('Tempo esgotado na autorização do GitHub.')); }
    }, timeoutMs);
  });
}

/** Providers Git que suportam o fluxo OAuth "conectar com sua conta". */
export const OAUTH_GIT_PROVIDERS = ['github', 'bitbucket'];

/**
 * useGitOAuthConnect — fluxo "conectar com sua conta" (GitHub ou Bitbucket).
 * `mutate(provider)`:
 *   1) GET /integrations/git/{provider}/authorize → authUrl (+ state no Redis);
 *   2) popup autoriza no provider → devolve code+state;
 *   3) POST /integrations/git/{provider}/exchange → troca por token e cifra no cofre.
 * O token conectado passa a ter precedência sobre as credenciais globais do .env.
 */
export function useGitOAuthConnect() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'Conectando sua conta…' },
    mutationFn: async (provider) => {
      const { authUrl } = await api.get(`/integrations/git/${provider}/authorize`);
      const { code, state } = await runOAuthPopup(authUrl);
      return api.post(`/integrations/git/${provider}/exchange`, { code, state });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.all() }),
  });
}
